import React, { Component } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import withStyles from '@mui/styles/withStyles';
import styles from '../AddEventDialog/styles';

import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InboundIcon from '@mui/icons-material/FlightLand';
import OutboundIcon from '@mui/icons-material/FlightTakeoff';
import { Button, Icon, Paper } from '@mui/material';
import Typography from '@mui/material/Typography';

import { TimePicker, DatePicker } from '@mui/x-date-pickers';

import { FormTextField, FormSelect, FormNumberField } from '../Form';
import { positiveInteger, required } from '../Form/validators';
import WeekDaysSelect from './WeekDaysSelect';
import {
  getValidEventRange,
  dateToUtcISOString,
  getSeason,
} from '../../helpers/dates';

const initialEvent = {
  inbound: {
    airport: '',
    flightNumber: '',
    STA: null,
    flightType: 'J',
  },
  outbound: {
    airport: '',
    flightNumber: '',
    STD: null,
    flightType: 'J',
  },
  start: null,
  end: null,
  weekDays: {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  },
  aircraftType: '',
  chairs: ' ',
};
const initialValues = {
  airline: '',
  airport: '',
  season: '',
  year: null,
  events: [
    {
      inbound: {
        airport: '',
        flightNumber: '',
        STA: null,
        flightType: 'J',
      },
      outbound: {
        airport: '',
        flightNumber: '',
        STD: null,
        flightType: 'J',
      },
      start: null,
      end: null,
      weekDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
      aircraftType: '',
      chairs: ' ',
    },
  ],
};

const flightTypes = [
  {
    name: 'A - Additional Cargo/Mail',
    value: 'A',
  },
  {
    name: 'C - Charter – Passenger only',
    value: 'C',
  },
  {
    name: 'E - Special VIP Flight (FAA/Government)',
    value: 'E',
  },
  {
    name: 'F - Scheduled – Cargo and/or Mail',
    value: 'F',
  },
  {
    name: 'G - Additional Flights – Passenger Normal Service',
    value: 'G',
  },
  {
    name: 'H - Charter – Cargo and/or Mail',
    value: 'H',
  },
  {
    name: 'I - Ambulance Flight',
    value: 'I',
  },
  {
    name: 'J - Scheduled – Passenger Normal Service',
    value: 'J',
  },
  {
    name: 'K - Training Flights',
    value: 'K',
  },
  {
    name: 'M - Mail Service',
    value: 'M',
  },
  {
    name: 'O - Charter requiring special handling (e.g. migrants, immigrants)',
    value: 'O',
  },
  {
    name: 'P - Positioning Flights – Non Revenue (ferry/delivery/demo)',
    value: 'P',
  },
  {
    name: 'T - Technical Test',
    value: 'T',
  },
  {
    name: 'W - Military',
    value: 'W',
  },
  {
    name: 'W - Technical Stop',
    value: 'X',
  },
];

class AddEventForm extends Component {
  state = {
    season: null,
    year: null,
    maxDate: undefined,
    minDate: undefined,
  };

  handleFromValueChange = date => {
    if (!date) return {};
    const { season, year } = getSeason(date.toDate());
    const range = getValidEventRange(season, year);
    this.setState({
      minDate: range.minDate,
      maxDate: range.maxDate,
    });
    return { season, year };
  };

  handleClose = () => {
    const { onClose } = this.props;
    onClose?.();
  };

  handleSubmit = async values => {
    const { onSubmit } = this.props;
    const events = values.events.map(event => {
      return {
        ...event,
        start: dateToUtcISOString(event.start.toDate()),
        end: dateToUtcISOString(event.end.toDate()),
        chairs: event.chairs.toString(),
        inbound: {
          ...event.inbound,
          STA: dateToUtcISOString(event.inbound.STA.toDate()),
        },
        outbound: {
          ...event.outbound,
          STD: dateToUtcISOString(event.outbound.STD.toDate()),
        },
      };
    });
    const formValues = {
      ...values,
      events,
      year: dateToUtcISOString(new Date(values.year, 0, 1)),
    };
    try {
      await onSubmit(formValues, values);
    } catch (e) {
      alert('Could not create events due to the error.');
    }
  };

  render() {
    const { airports, classes, t } = this.props;
    const { maxDate, minDate } = this.state;
    const airpotsSelect = airports.map(item => {
      return {
        name: item.name,
        value: item.name,
      };
    });
    return (
      <Formik
        initialValues={this.props.formValues ?? initialValues}
        onSubmit={this.handleSubmit}
        enableReinitialize
      >
        {({ submitForm, values, setFieldValue, isValid }) => (
          <>
            <DialogContent className={classes.dialog}>
              <Form className={classes.form}>
                <Paper className={classes.eventInfo}>
                  <FormTextField
                    label={t('airline_letter_code')}
                    size={2}
                    inputProps={{
                      maxLength: 2,
                    }}
                    validate={required}
                    value={values.airline}
                    onChange={event => {
                      setFieldValue(
                        'airline',
                        event.target.value.toString().toUpperCase()
                      );
                    }}
                    error={!values.airline}
                  />
                  <FormSelect
                    name="airport"
                    items={airpotsSelect}
                    label={t('Airport')}
                    validate={required}
                    required
                    size={1}
                  />
                  {!!values.year && values.season && (
                    <Typography>
                      Season : {values.season} {values.year}
                    </Typography>
                  )}
                </Paper>

                <FieldArray name="events">
                  {({ remove, push }) => (
                    <div className={classes.events}>
                      {values.events.length > 0 &&
                        values.events.map((event, index) => (
                          <Paper className={classes.eventContainer}>
                            <div className={classes.event}>
                              <div className={classes.arrivalDepature}>
                                <Paper className={classes.flight}>
                                  <InboundIcon />
                                  <FormTextField
                                    label={t('Origin')}
                                    size={2}
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                    value={values.events[index].inbound.airport}
                                    onChange={event => {
                                      setFieldValue(
                                        `events.${index}.inbound.airport`,
                                        event.target.value.toUpperCase()
                                      );
                                    }}
                                    validate={required}
                                    error={
                                      !values.events[index].inbound.airport
                                    }
                                  />
                                  <FormTextField
                                    label={t('Flight_number')}
                                    name={`events.${index}.inbound.flightNumber`}
                                    size={3}
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                    validate={required}
                                  />
                                  <TimePicker
                                    className={classes.timePicker}
                                    label="STA (UTC)"
                                    ampm={false}
                                    format="HH:mm"
                                    value={values.events[index].inbound.STA}
                                    validate={required}
                                    slotProps={{
                                      field: { variant: 'standard' },
                                    }}
                                    onChange={date =>
                                      setFieldValue(
                                        `events.${index}.inbound.STA`,
                                        date
                                      )
                                    }
                                    error={!values.events[index].inbound.STA}
                                  />
                                  <FormSelect
                                    name={`events.${index}.inbound.flightType`}
                                    items={flightTypes}
                                    label={t('Type_of_Flight')}
                                    size={2}
                                    renderValue={value => value}
                                    validate={required}
                                  />
                                </Paper>
                                <Paper className={classes.flight}>
                                  <OutboundIcon />
                                  <FormTextField
                                    label={t('Destination')}
                                    size={2}
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                    value={
                                      values.events[index].outbound.airport
                                    }
                                    onChange={event => {
                                      setFieldValue(
                                        `events.${index}.outbound.airport`,
                                        event.target.value.toUpperCase()
                                      );
                                    }}
                                    validate={required}
                                    error={
                                      !values.events[index].outbound.airport
                                    }
                                  />
                                  <FormTextField
                                    label={t('Flight_number')}
                                    name={`events.${index}.outbound.flightNumber`}
                                    size={3}
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                    validate={required}
                                  />
                                  <TimePicker
                                    className={classes.timePicker}
                                    label="STD (UTC)"
                                    ampm={false}
                                    format="HH:mm"
                                    value={values.events[index].outbound.STD}
                                    slotProps={{
                                      field: { variant: 'standard' },
                                    }}
                                    onChange={date =>
                                      setFieldValue(
                                        `events.${index}.outbound.STD`,
                                        date
                                      )
                                    }
                                    validate={required}
                                    error={!values.events[index].outbound.STD}
                                  />
                                  <FormSelect
                                    name={`events.${index}.outbound.flightType`}
                                    items={flightTypes}
                                    label={t('Type_of_Flight')}
                                    size={2}
                                    renderValue={value => value}
                                    validate={required}
                                  />
                                </Paper>
                              </div>
                              <Paper className={classes.period}>
                                <Typography
                                  variant="subtitle1"
                                  className={classes.subtitle}
                                >
                                  {t('Period_of_operation')}
                                </Typography>
                                <div className={classes.range}>
                                  <DatePicker
                                    className={classes.datePicker}
                                    defaultValue={undefined}
                                    value={
                                      values.events[index].start
                                        ? moment(values.events[index].start)
                                        : null
                                    }
                                    format="DD/MM/YYYY"
                                    label={t('Start_date')}
                                    validate={required}
                                    error={!values.events[index].start}
                                    slotProps={{
                                      field: { variant: 'standard' },
                                    }}
                                    onChange={date => {
                                      setFieldValue(
                                        `events.${index}.start`,
                                        date
                                      );
                                      const { season, year } =
                                        this.handleFromValueChange(date);
                                      setFieldValue('season', season);
                                      setFieldValue('year', year);
                                    }}
                                  />
                                  <DatePicker
                                    className={classes.datePicker}
                                    value={
                                      values.events[index].start
                                        ? moment(values.events[index].end)
                                        : null
                                    }
                                    minDate={moment(minDate)}
                                    maxDate={moment(maxDate)}
                                    format="DD/MM/YYYY"
                                    label={t('End_date')}
                                    validate={required}
                                    error={!values.events[index].end}
                                    slotProps={{
                                      field: { variant: 'standard' },
                                    }}
                                    onChange={date =>
                                      setFieldValue(`events.${index}.end`, date)
                                    }
                                  />
                                </div>
                                <Typography
                                  variant="subtitle1"
                                  className={classes.subtitle}
                                >
                                  {t('Days_of_operation')}
                                </Typography>
                                <WeekDaysSelect
                                  value={values.events[index].weekDays}
                                  onChange={days =>
                                    setFieldValue(
                                      `events.${index}.weekDays`,
                                      days
                                    )
                                  }
                                />
                              </Paper>
                              <Paper className={classes.details}>
                                <FormTextField
                                  label={t('Aircraft_type')}
                                  value={values.events[index].aircraftType}
                                  inputProps={{
                                    maxLength: 3,
                                  }}
                                  onChange={e =>
                                    setFieldValue(
                                      `events.${index}.aircraftType`,
                                      e.target.value
                                    )
                                  }
                                  fullWidth
                                />
                                <FormNumberField
                                  inputProps={{
                                    max: 999,
                                    maxLength: 3,
                                  }}
                                  allowedInputValue={positiveInteger}
                                  label={t('Total_no_of_seats')}
                                  name={`events.${index}.chairs`}
                                  fullWidth
                                />
                              </Paper>
                            </div>
                            {index > 0 && (
                              <Button
                                className={classes.deleteButton}
                                color="secondary"
                                fullWidth
                                variant="outlined"
                                startIcon={<Icon>delete</Icon>}
                                onClick={() => remove(index)}
                              >
                                {t('Delete')}
                              </Button>
                            )}
                          </Paper>
                        ))}
                      <Button
                        color="primary"
                        fullWidth
                        variant="outlined"
                        startIcon={<Icon>add</Icon>}
                        onClick={() => push(initialEvent)}
                      >
                        {t('Add_Slot')}
                      </Button>
                    </div>
                  )}
                </FieldArray>
              </Form>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleClose} color="primary">
                {t('Cancel')}
              </Button>
              <Button onClick={submitForm} color="primary" disabled={!isValid}>
                {t('Generate_slots')}
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    );
  }
}

export default withTranslation()(withStyles(styles)(AddEventForm));
